import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { auth, dbAccounts } from "../../../../backend/firebaseConfig";
import { assertChargeDatesAreOpen } from "../utils/closedChargeMonths";

const COLLECTION_NAME = "IORecipientMappings";
const mappingsRef = collection(dbAccounts, COLLECTION_NAME);

const clean = (value) => String(value ?? "").trim();
const sourceKey = (value) => clean(value).toLowerCase();
const sourceDocumentId = (value) =>
  `name-${sourceKey(value).replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 120)}`;

const mappingsFromSnapshot = (snapshot) => {
  const mappings = {};
  snapshot.docs.forEach((mappingDocument) => {
    const data = mappingDocument.data() || {};
    const recipientCrmId = clean(data.recipientCrmId);
    const mappingKey = recipientCrmId || clean(data.sourceKey) || mappingDocument.id;
    if (!mappingKey || data.active === false) return;
    mappings[mappingKey.toLowerCase()] = {
      ...data,
      id: mappingDocument.id,
      recipientCrmId,
    };
  });
  return mappings;
};

export const subscribeIORecipientMappings = (onData, onError) =>
  onSnapshot(
    mappingsRef,
    (snapshot) => onData(mappingsFromSnapshot(snapshot)),
    onError,
  );

export const getIORecipientMappings = async () =>
  mappingsFromSnapshot(await getDocs(mappingsRef));

const recipientMappingData = ({
  recipientCrmId,
  sourceName,
  target,
}) => {
  const sourceId = clean(recipientCrmId);
  const nameKey = sourceKey(sourceName);
  if (!sourceId && !nameKey) throw new Error("The IO recipient is missing.");
  if (!target || !["adviser", "introducer"].includes(target.type)) {
    throw new Error("Select an adviser or IO introducer.");
  }

  const user = auth.currentUser;
  const adviser = target.type === "adviser";
  return {
    recipientCrmId: sourceId,
    sourceName: clean(sourceName),
    sourceKey: nameKey,
    targetType: target.type,
    targetId: clean(target.targetId),
    targetName: clean(target.name),
    assignedAdviserEmail: adviser ? clean(target.email || target.targetId).toLowerCase() : null,
    assignedAdviserName: adviser ? clean(target.name) : null,
    ioIntroducerId: adviser ? null : clean(target.targetId),
    ioName: adviser ? null : clean(target.name),
    entityType: target.type,
    displayName: clean(target.name),
    active: true,
    updatedAt: serverTimestamp(),
    updatedBy: {
      uid: user?.uid || "",
      email: user?.email || "",
      name: user?.displayName || user?.email || "",
    },
  };
};

export const saveIORecipientMapping = async ({
  recipientCrmId,
  sourceName,
  target,
}) => {
  const data = recipientMappingData({ recipientCrmId, sourceName, target });
  await setDoc(
    doc(mappingsRef, data.recipientCrmId || sourceDocumentId(sourceName)),
    data,
    { merge: true },
  );
};

export const saveIORecipientMappings = async ({
  recipientCrmIds,
  sourceName,
  target,
}) => {
  const ids = [...new Set((recipientCrmIds || []).map(clean).filter(Boolean))];
  if (!ids.length) throw new Error("The IO recipient records are missing.");

  // Leave room below Firestore's 500-operation batch limit.
  for (let offset = 0; offset < ids.length; offset += 450) {
    const batch = writeBatch(dbAccounts);
    ids.slice(offset, offset + 450).forEach((recipientCrmId) => {
      batch.set(
        doc(mappingsRef, recipientCrmId),
        recipientMappingData({ recipientCrmId, sourceName, target }),
        { merge: true },
      );
    });
    await batch.commit();
  }
};

export const separateIORecipientMapping = ({
  recipientCrmId,
  sourceName,
}) => {
  const sourceId = clean(recipientCrmId);
  const name = clean(sourceName) || `IO Introducer ${sourceId}`;
  if (!sourceId) throw new Error("The IO recipient is missing.");
  return saveIORecipientMapping({
    recipientCrmId: sourceId,
    sourceName: name,
    target: {
      type: "introducer",
      targetId: sourceId,
      name,
    },
  });
};

export const saveManualPaymentRecipientMapping = async ({
  paymentIds,
  sourceName,
  target,
}) => {
  const ids = [...new Set((paymentIds || []).map(clean).filter(Boolean))];
  const nameKey = sourceKey(sourceName);
  if (!ids.length) throw new Error("The payment records are missing.");
  if (!nameKey) throw new Error("The unknown recipient name is missing.");
  if (!target || !["adviser", "introducer"].includes(target.type)) {
    throw new Error("Select an adviser or IO introducer.");
  }

  const user = auth.currentUser;
  const adviser = target.type === "adviser";
  const targetId = clean(target.targetId);
  const targetName = clean(target.name);
  const adviserEmail = adviser
    ? clean(target.email || target.targetId).toLowerCase()
    : "";
  const updatedBy = {
    uid: user?.uid || "",
    email: user?.email || "",
    name: user?.displayName || user?.email || "",
  };
  const paymentSnapshots = await Promise.all(
    ids.map((paymentId) =>
      getDoc(doc(dbAccounts, "MonthlyManualPayments", paymentId)),
    ),
  );
  if (paymentSnapshots.some((snapshot) => !snapshot.exists())) {
    throw new Error("One or more payment records could not be found.");
  }
  await assertChargeDatesAreOpen(
    paymentSnapshots.map((snapshot) => snapshot.data().date),
  );
  const batch = writeBatch(dbAccounts);

  batch.set(
    doc(mappingsRef, sourceDocumentId(sourceName)),
    {
      recipientCrmId: "",
      sourceName: clean(sourceName),
      sourceKey: nameKey,
      targetType: target.type,
      targetId,
      targetName,
      assignedAdviserEmail: adviser ? adviserEmail : null,
      assignedAdviserName: adviser ? targetName : null,
      ioIntroducerId: adviser ? null : targetId,
      ioName: adviser ? null : targetName,
      entityType: target.type,
      displayName: targetName,
      active: true,
      updatedAt: serverTimestamp(),
      updatedBy,
    },
    { merge: true },
  );

  ids.forEach((paymentId) => {
    batch.update(doc(dbAccounts, "MonthlyManualPayments", paymentId), {
      adviserEmail,
      adviserName: adviser ? targetName : "",
      adviserId: adviser ? clean(target.adviser?.id || target.adviserId) : "",
      brandName: adviser
        ? clean(target.adviser?.brandName || target.brandName || targetName)
        : targetName,
      mappedEntityType: target.type,
      mappedEntityId: targetId,
      mappedEntityName: targetName,
      ioIntroducerId: adviser ? null : targetId,
      updatedAt: serverTimestamp(),
      updatedBy,
    });
  });

  await batch.commit();
};
